import React from "react";
import { Link } from "react-router-dom";
import Spinner from "./Spinner";

// The site's one button. Brand colors come from the CSS variables in
// index.css, so partner themes recolor it too.
const VARIANTS = {
  primary:
    "bg-mainButton text-white hover:bg-mainButtonShadow shadow-[0_10px_24px_-10px_rgb(var(--c-mainButton)/0.8)]",
  gold: "bg-primary text-header hover:bg-mainText",
  secondary:
    "bg-white/10 text-white border border-white/15 hover:bg-white/15 hover:border-white/25",
  light: "bg-white text-header hover:bg-white/90",
  ghost: "text-white/80 hover:text-white hover:bg-white/10",
  danger: "bg-danger text-white hover:brightness-95",
};

const SIZES = {
  sm: "h-9 px-3.5 text-sm rounded-lg gap-1.5",
  md: "h-11 px-5 text-[15px] rounded-xl gap-2",
  lg: "h-14 px-7 text-lg rounded-2xl gap-2.5",
};

export const buttonClass = ({
  variant = "primary",
  size = "md",
  block = false,
  className = "",
} = {}) =>
  [
    "inline-flex items-center justify-center font-bold whitespace-nowrap select-none",
    "transition-[background-color,border-color,color,filter] duration-150",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-header",
    "disabled:opacity-60 disabled:cursor-not-allowed",
    VARIANTS[variant] || VARIANTS.primary,
    SIZES[size] || SIZES.md,
    block ? "w-full" : "",
    className,
  ].join(" ");

// `to` renders a router Link, `href` an external link (new tab), otherwise a
// <button>. `icon` sits before the label (on the right in RTL).
const Button = ({
  variant,
  size,
  block,
  className,
  to,
  href,
  state,
  loading = false,
  icon = null,
  iconEnd = null,
  type = "button",
  disabled,
  children,
  ...rest
}) => {
  const cls = buttonClass({ variant, size, block, className });
  const content = loading ? (
    <Spinner />
  ) : (
    <>
      {icon}
      {children != null && <span>{children}</span>}
      {iconEnd}
    </>
  );

  if (to) {
    return (
      <Link to={to} state={state} className={cls} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cls}
        {...rest}
      >
        {content}
      </a>
    );
  }
  return (
    <button
      type={type}
      className={cls}
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {content}
    </button>
  );
};

export default Button;
